import type { ComponentStyle } from '@deckspace/types';

interface ResolvedStyle {
  className: string;
  style: Record<string, string | number>;
}

const BG_MAP: Record<string, string> = {
  surface: 'bg-[hsl(var(--background))]',
  'surface-raised': 'bg-[hsl(var(--card))]',
  primary: 'bg-[hsl(var(--primary))]',
  accent: 'bg-[hsl(var(--accent))]',
  muted: 'bg-[hsl(var(--muted))]',
  transparent: 'bg-transparent',
};

const COLOR_MAP: Record<string, string> = {
  foreground: 'text-[hsl(var(--foreground))]',
  muted: 'text-[hsl(var(--muted-foreground))]',
  primary: 'text-[hsl(var(--primary))]',
  accent: 'text-[hsl(var(--accent-foreground))]',
  success: 'text-emerald-500',
  warning: 'text-amber-500',
  danger: 'text-red-500',
};

const SPACING_MAP: Record<string, string> = {
  none: '0',
  xs: '1',
  sm: '2',
  md: '4',
  lg: '6',
  xl: '8',
};

const RADIUS_MAP: Record<string, string> = {
  none: 'rounded-none',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  full: 'rounded-full',
};

const SHADOW_MAP: Record<string, string> = {
  none: 'shadow-none',
  sm: 'shadow-sm',
  md: 'shadow-md',
  lg: 'shadow-lg',
};

const BORDER_MAP: Record<string, string> = {
  none: 'border-0',
  default: 'border border-[hsl(var(--border))]',
  muted: 'border border-[hsl(var(--muted))]',
  accent: 'border border-[hsl(var(--accent))]',
};

export function resolveStyle(style: ComponentStyle | undefined): ResolvedStyle {
  if (!style) return { className: '', style: {} };

  const classes: string[] = [];
  const inlineStyle: Record<string, string | number> = {};

  if (style.bg) classes.push(BG_MAP[style.bg] ?? '');
  if (style.color) classes.push(COLOR_MAP[style.color] ?? '');
  if (style.padding) classes.push(`p-${SPACING_MAP[style.padding]}`);
  if (style.margin) classes.push(`m-${SPACING_MAP[style.margin]}`);

  if (style.gap !== undefined) {
    if (typeof style.gap === 'number') {
      inlineStyle.gap = `${style.gap}px`;
    } else {
      classes.push(`gap-${SPACING_MAP[style.gap]}`);
    }
  }

  if (style.radius) classes.push(RADIUS_MAP[style.radius] ?? '');
  if (style.shadow) classes.push(SHADOW_MAP[style.shadow] ?? '');
  if (style.border) classes.push(BORDER_MAP[style.border] ?? '');

  if (style.width !== undefined) {
    if (typeof style.width === 'number') {
      inlineStyle.width = `${style.width}px`;
    } else if (style.width === 'full') {
      classes.push('w-full');
    } else if (style.width === 'half') {
      classes.push('w-1/2');
    } else if (style.width === 'auto') {
      classes.push('w-auto');
    }
  }

  if (style.height !== undefined) {
    if (typeof style.height === 'number') {
      inlineStyle.height = `${style.height}px`;
    } else if (style.height === 'full') {
      classes.push('h-full');
    } else if (style.height === 'auto') {
      classes.push('h-auto');
    }
  }

  if (style.minHeight !== undefined) {
    inlineStyle.minHeight = `${style.minHeight}px`;
  }

  if (style.opacity !== undefined) {
    inlineStyle.opacity = style.opacity;
  }

  return {
    className: classes.filter(Boolean).join(' '),
    style: inlineStyle,
  };
}
